import React, { useState, useEffect, useMemo, useCallback } from "react";
import { MdSchool, MdRefresh, MdEventAvailable, MdCalendarToday } from "react-icons/md";
import { getTrainingTopics } from "api/trainingTopics";

function UpcomingTrainingEvents() {
  const [trainingTopics, setTrainingTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTrainingTopics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getTrainingTopics();
      setTrainingTopics(result.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load training topics");
      setTrainingTopics([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrainingTopics();
  }, [fetchTrainingTopics]);

  const upcomingEvents = useMemo(() => {
    const now = new Date();
    return trainingTopics
      .filter((topic) => topic.start_date && new Date(topic.start_date) >= now)
      .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
  }, [trainingTopics]);

  if (loading) {
    return (
      <div className="p-4 bg-white dark:bg-slate-900 rounded-2xl shadow-sm text-center">
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading upcoming training events…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-2xl shadow-sm text-center space-y-2">
        <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
        <button
          type="button"
          onClick={fetchTrainingTopics}
          className="text-sm font-bold text-[#0e76a8] hover:underline"
        >
          <MdRefresh className="inline-block mr-1" /> Retry
        </button>
      </div>
    );
  }

  if (upcomingEvents.length === 0) {
    return (
      <div className="p-4 bg-white dark:bg-slate-900 rounded-2xl shadow-sm text-center">
        <p className="text-sm text-slate-500 dark:text-slate-400">No upcoming training events.</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm p-4">
      <h3 className="font-black text-slate-900 dark:text-white text-lg mb-4 flex items-center gap-2">
        <MdEventAvailable className="text-xl text-[#0e76a8]" /> Upcoming Training
      </h3>
      <div className="space-y-4">
        {upcomingEvents.map((topic) => (
          <div key={topic.id} className="border border-slate-200 dark:border-slate-700 rounded-xl p-3 flex items-start gap-3">
            <div className="p-2 rounded-lg bg-[#f27405]/10 text-[#f27405] shrink-0">
              <MdCalendarToday className="text-lg" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-slate-900 dark:text-white text-sm">{topic.name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{topic.definition}</p>
              {topic.start_date && (
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                  Starts: {new Date(topic.start_date).toLocaleDateString("en-GB", { weekday: "short", year: "numeric", month: "short", day: "numeric" })}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default UpcomingTrainingEvents;
